import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../../api/axiosConfig';
import { downloadBlob } from './format';

const thisMonthISO = () => new Date().toISOString().slice(0, 7);

const STATUS_CELL = { present: '✓', half: '½', absent: '', weekend: '' };
const STATUS_TITLE = {
    present: 'Present', half: 'Half Day', absent: 'Absent', weekend: 'Weekend / holiday',
};

/** Hover text for one cell — "Present — approximate calculation" etc. */
function cellTitle(cell) {
    const base = STATUS_TITLE[cell.status];
    if (cell.status === 'weekend' || !cell.approx) return base;
    return `${base} — approximate calculation`;
}

/**
 * Month attendance grid: one row per employee, one column per calendar day.
 *
 * Deliberately kept to a ✓ / ½ / blank cell — the Daily and Monthly tabs
 * already show hours in full; this tab exists to answer "who was here, on
 * which days" at a glance across a whole month, which a row of numbers
 * cannot do. The Detail export carries every underlying figure.
 */
export default function SheetTab() {
    const [month, setMonth] = useState(thisMonthISO());
    const [department, setDepartment] = useState('all');
    const [hideNoAgentData, setHideNoAgentData] = useState(true);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        return axiosInstance
            .get('/api/working-time/sheet', { params: { month, department, hideNoAgentData } })
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.message || 'Could not load the attendance sheet'))
            .finally(() => setLoading(false));
    }, [month, department, hideNoAgentData]);

    useEffect(() => { load(); }, [load]);

    const departments = (data && data.departments) || [];
    const days = useMemo(
        () => Array.from({ length: (data && data.calendarDays) || 0 }, (_, i) => i + 1),
        [data]
    );

    const download = (format) => {
        const extension = format === 'pdf' ? 'pdf' : 'xlsx';
        axiosInstance
            .get('/api/working-time/sheet/export', {
                params: { month, department, hideNoAgentData, format }, responseType: 'blob'
            })
            .then((r) => downloadBlob(r.data, `attendance-sheet-${month}.${extension}`))
            .catch(() => setError('Could not download the export'));
    };

    return (
        <div>
            <div className="wt-toolbar">
                <div className="wt-field">
                    <label htmlFor="wt-sheet-month">Month</label>
                    <input
                        id="wt-sheet-month"
                        type="month"
                        value={month}
                        onChange={(e) => setMonth(e.target.value)}
                    />
                </div>
                <div className="wt-field">
                    <label htmlFor="wt-sheet-dept">Department</label>
                    <select
                        id="wt-sheet-dept"
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                    >
                        <option value="all">All departments</option>
                        {departments.map((d) => (
                            <option key={d._id} value={d._id}>{d.name}</option>
                        ))}
                    </select>
                </div>
                <label className="wt-check">
                    <input
                        type="checkbox"
                        checked={hideNoAgentData}
                        onChange={(e) => setHideNoAgentData(e.target.checked)}
                    />
                    Hide employees with no agent data
                </label>
                <button type="button" className="wt-btn" onClick={() => load()}>Refresh</button>
                <button type="button" className="wt-btn primary" onClick={() => download('xlsx')}>
                    Excel
                </button>
                <button type="button" className="wt-btn" onClick={() => download('pdf')}>
                    PDF
                </button>
            </div>

            {data && (
                <div className="wt-sheet-legend">
                    <span className="wt-sheet-legend-item">
                        <span className="wt-sheet-glyph present">✓</span> Present ({data.settings.presentThresholdHours}h+)
                    </span>
                    <span className="wt-sheet-legend-item">
                        <span className="wt-sheet-glyph half">½</span> Half Day ({data.settings.halfDayThresholdHours}h+)
                    </span>
                    <span className="wt-sheet-legend-item">
                        <span className="wt-sheet-glyph" /> Absent
                    </span>
                    <span className="wt-sheet-legend-item">
                        <sup className="wt-sheet-approx">†</sup> Approximate — incomplete agent data for that day
                    </span>
                    <span className="wt-sheet-legend-item">
                        <span className="wt-sheet-swatch weekend" /> Weekend / holiday
                    </span>
                </div>
            )}
            {data && (
                <p className="wt-note" style={{ marginTop: 0, marginBottom: 12 }}>
                    Counts only {String(data.settings.officeStartHour).padStart(2, '0')}:00–
                    {String(data.settings.officeEndHour).padStart(2, '0')}:00 Qatar time, capped at{' '}
                    {data.settings.maxDailyHours}h/day. A day marked † could not be measured exactly (offline
                    catch-up, or no agent data at all that day) — common for history from before per-minute
                    sampling was introduced; a fresh day should rarely need it. Exact hours for every day are in
                    the exported Detail sheet.
                </p>
            )}

            {error && <div className="wt-error">{error}</div>}
            {loading && <div className="wt-empty">Loading…</div>}

            {!loading && data && data.rows.length === 0 && (
                <div className="wt-empty">No employees match this filter.</div>
            )}

            {!loading && data && data.rows.length > 0 && (
                <div className="wt-punch-scroll">
                    <table className="wt-sheet-table">
                        <thead>
                            <tr>
                                <th className="wt-sheet-name-col">Employee</th>
                                <th className="wt-sheet-dept-col">Dept</th>
                                {days.map((d) => <th key={d} className="wt-sheet-day-col">{d}</th>)}
                                <th className="wt-sheet-summary-col">P</th>
                                <th className="wt-sheet-summary-col">H</th>
                                <th className="wt-sheet-summary-col">A</th>
                                <th className="wt-sheet-summary-col">Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((r, rowIndex) => (
                                <tr key={r.userId} className={rowIndex % 2 === 1 ? 'wt-sheet-alt' : ''}>
                                    <td className="wt-sheet-name-col">
                                        {r.name}
                                        <div className="wt-sheet-code">{r.employeeCode}</div>
                                    </td>
                                    <td className="wt-sheet-dept-col">{r.department}</td>
                                    {days.map((d) => {
                                        const cell = r.days[d];
                                        const cls = ['wt-sheet-day-col', `wt-sheet-cell-${cell.status}`];
                                        return (
                                            <td key={d} className={cls.join(' ')} title={cellTitle(cell)}>
                                                {STATUS_CELL[cell.status]}
                                                {cell.approx && cell.status !== 'weekend' && (
                                                    <sup className="wt-sheet-approx">†</sup>
                                                )}
                                            </td>
                                        );
                                    })}
                                    <td className="wt-sheet-summary-col">{r.presentCount}</td>
                                    <td className="wt-sheet-summary-col">{r.halfDayCount}</td>
                                    <td className="wt-sheet-summary-col">{r.absentCount}</td>
                                    <td className="wt-sheet-summary-col wt-sheet-total-col">{r.totalHours}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
